import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useEffect, useCallback } from "react";
import { GlobalService } from "../modules/shared";
import VerificationService, { VerificationResult } from "../services/verificationService";
import { useVerificationContext } from "../contexts/VerificationContext";
import { refreshVerificationData as refreshUtil } from "../utils/verificationUtils";
import i18n from "../i18n";

/**
 * Custom hook for verification functionality
 * Can be used in any component to check verification status and handle restrictions
 */
export const useVerification = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    // Get data from Redux store
    const { user } = useSelector((state: any) => state.auth);
    const verificationData = useSelector((state: any) => state.client.verificationData);
    const company = useSelector((state: any) => state.client.company);
    
    // Try to get context refresh function, fallback to local implementation
    let contextRefresh: (() => Promise<void>) | null = null;
    try {
        const context = useVerificationContext();
        contextRefresh = context.refreshVerificationData;
    } catch {
        // Context not available, will use local implementation
    }

    // Process verification data (handle array format)
    const processedVerificationData = Array.isArray(verificationData) && verificationData.length > 0
        ? verificationData[0]
        : verificationData ?? null;

    /**
     * Refresh verification data from the server
     * Uses context refresh if available, otherwise falls back to local implementation with cooldown
     */
    const refreshVerificationData = useCallback(async (force: boolean = false) => {
        if (contextRefresh) {
            return contextRefresh();
        }
        
        if (user?.id) {
            try {
                await refreshUtil(Number(user.id), force);
            } catch (error) {
                console.error("Failed to refresh verification data:", error);
            }
        }
    }, [contextRefresh, user?.id]);

    /**
     * Auto-refresh verification data when user changes or on mount
     * Only if context is not available (context handles this automatically)
     * Only fetch if we don't already have verification data
     */
    useEffect(() => {
        if (!contextRefresh && user?.id && !processedVerificationData) {
            refreshVerificationData();
        }
    }, [user?.id, contextRefresh, processedVerificationData]);

    /**
     * Get comprehensive verification result
     */
    const getVerificationResult = (): VerificationResult => {
        return VerificationService.getVerificationResult(
            user,
            processedVerificationData,
            company
        );
    };

    /**
     * Check if user can purchase/place orders
     */
    const canPurchase = (): boolean => {
        return VerificationService.canPurchase(
            user,
            processedVerificationData,
            company
        );
    };

    /**
     * Check if user is fully verified
     */
    const isFullyVerified = (): boolean => {
        return VerificationService.isFullyVerified(
            user,
            processedVerificationData,
            company
        );
    };

    /**
     * Check if user is verified based on specific criteria
     */
    const isVerified = (): boolean => {
        return VerificationService.isVerified(
            user,
            processedVerificationData,
            company
        );
    };

    /**
     * Get verification progress percentage
     */
    const getProgress = (): number => {
        return VerificationService.calculateVerificationProgress(
            user,
            processedVerificationData,
            company
        );
    };

    /**
     * Get missing verification steps
     */
    const getMissingSteps = () => {
        return VerificationService.getMissingSteps(
            user,
            processedVerificationData,
            company
        );
    };

    /**
     * Handle purchase restriction - shows dialog and redirects if not verified
     * @param customMessage Optional custom message to show
     * @returns true if can proceed, false if blocked
     */
    const handlePurchaseRestriction = (customMessage?: string): boolean => {
        const result = getVerificationResult();

        if (!result.canPurchase) {
            if (customMessage) {
                GlobalService.showToast("warning", i18n.t("toast.verification.required"), customMessage);
            } else {
                VerificationService.showVerificationDialog(
                    result.missingSteps,
                    navigate,
                    GlobalService.showToast
                );
            }
            return false;
        }

        return true;
    };

    /**
     * Show verification dialog with custom message and redirect to verify page
     */
    const showVerificationDialog = (message: string) => {
        GlobalService.showToast("warning", i18n.t("toast.verification.required"), message);
        navigate("/verify");
    };

    /**
     * Navigate to verification page
     */
    const goToVerification = () => {
        navigate("/verify");
    };

    return {
        // Data
        user,
        verificationData: processedVerificationData,
        company,
        
        // Status checks
        getVerificationResult,
        canPurchase,
        isFullyVerified,
        isVerified,
        getProgress,
        getMissingSteps,
        
        // Actions
        handlePurchaseRestriction,
        showVerificationDialog,
        goToVerification,
        refreshVerificationData,
    };
};

export default useVerification;
